import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { HardDrive, Boxes, KeyRound, ShieldCheck, Sparkles } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyServices } from '@/api/services';
import { useProviders } from '@/api/service-provisioning';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { useSelfServeActivation } from '../activation-mode';
import { ServiceContactSalesDialog } from '../components/ServiceContactSalesDialog';
import { STORAGE_PROVIDER, STORAGE_PRODUCT, storageConsoleRoute } from './storage-service';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

/**
 * Landing GATE for Object Storage at /services/oss, mirroring the SSL
 * WorkspaceGate: one look at the org's services decides what renders —
 *   - loading         → skeleton
 *   - service present  → redirect into its S3 console
 *   - none             → activation hero → launches the onboarding wizard
 * No per-service S3 query fires until a service exists.
 */
export function StorageLanding({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate();
  const { data: services = [], isLoading } = useMyServices();
  const { data: providers = [], isLoading: providersLoading } = useProviders();
  // Self-serve provisioning is gated (contact-sales for direct customers, on
  // while staff impersonate); when off the CTA opens a contact-sales dialog.
  const [contactOpen, setContactOpen] = useState(false);
  const selfServeActivation = useSelfServeActivation();

  const existing = useMemo(
    () => services.find((s) => s.provider?.name?.trim().toLowerCase() === STORAGE_PROVIDER),
    [services],
  );
  const available = useMemo(
    () => providers.some((p) => p.name?.trim().toLowerCase() === STORAGE_PROVIDER && p.status === 'active'),
    [providers],
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <Header onBack={onBack} />
        <Skeleton style={{ height: 200 }} />
      </Container>
    );
  }

  if (existing) {
    return <Navigate to={storageConsoleRoute(existing.id)} replace />;
  }

  return (
    <Container size="xl">
      <Header onBack={onBack} />
      <Activation
        unavailable={!providersLoading && !available}
        onActivate={
          selfServeActivation
            ? () => navigate('/services/oss/onboarding')
            : () => setContactOpen(true)
        }
      />
      <ServiceContactSalesDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        serviceLabel={STORAGE_PRODUCT.displayName}
        productGroup={PRODUCT_GROUP.storage}
      />
    </Container>
  );
}

function Header({ onBack }: { onBack?: () => void }) {
  return (
    <PageHeader
      icon={<HardDrive size={22} />}
      actions={
        onBack ? (
          <Button variant="ghost" onClick={onBack}>
            Back to services
          </Button>
        ) : undefined
      }
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          Object Storage
          <Badge tone="info" appearance="subtle" size="sm">
            Beta
          </Badge>
        </span>
      }
      description="S3-compatible object storage — buckets, access keys and versioning, on a dedicated account."
    />
  );
}

const PERKS = [
  { icon: <Boxes size={16} />, title: 'S3-compatible buckets', desc: 'Create buckets with versioning and object lock — works with any S3 tool or SDK.' },
  { icon: <KeyRound size={16} />, title: 'Access keys & policies', desc: 'Mint access keys and scope them with IAM policies for least-privilege access.' },
  { icon: <ShieldCheck size={16} />, title: 'A dedicated account', desc: 'A private storage account is provisioned for your organization on activation.' },
];

/** First-run onboarding hero — hands off to the setup wizard on click. */
function Activation({ unavailable, onActivate }: { unavailable: boolean; onActivate: () => void }) {
  if (unavailable) {
    return (
      <Alert tone="warning" title={`${STORAGE_PRODUCT.displayName} is not available yet`}>
        {STORAGE_PRODUCT.displayName} is not available for self-serve activation on your account yet.
        Please contact our team to enable it.
      </Alert>
    );
  }
  return (
    <div className="vn-ob-hero" style={heroWrap}>
      <OnboardingCss />

      <span className="vn-ob-halo" style={heroIcon} aria-hidden>
        <HardDrive size={30} />
      </span>

      <p style={heroEyebrow}>Object Storage</p>
      <h2 style={heroTitle}>Enable Object Storage for your organization</h2>
      <p style={heroLede}>
        Activate to provision a dedicated storage account, then create your first bucket, policy and
        access key — it only takes a moment.
      </p>

      <Button
        size="lg"
        startIcon={<Sparkles size={16} />}
        onClick={onActivate}
        style={{ marginTop: 'var(--vn-space-2)' }}
      >
        Activate Object Storage
      </Button>

      <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
        {PERKS.map((p) => (
          <div key={p.title} style={stepRow}>
            <span style={markerAccent}>{p.icon}</span>
            <div style={{ display: 'grid', gap: 2 }}>
              <p style={stepTitle}>{p.title}</p>
              <p style={stepDesc}>{p.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default StorageLanding;
